/* EDU-117 — détail d'une classe : roster élèves (ajout/mutation/désactivation) */
/* + enseignants associés (prof, matière). Direction only (RLS). Modales charte. */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Modal, FormField, SelectField } from '../components/modal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  Plus, ChalkboardTeacher,
  EnvelopeSimple, ArrowsLeftRight, Pause, Play, X, PencilSimple,
  Archive, WarningCircle, ArrowClockwise,
} from '@phosphor-icons/react';
import {
  getMySchoolId, listClassrooms, updateClassroom, listLevels, listSubjects,
  listStudents, addStudent, moveStudent, moveStudents, setStudentActive, deleteStudent, inviteParentOrTutor,
  listTeachers, listClassroomTeachers, assignTeacher, unassignTeacher,
  type Classroom, type Student, type SchoolTeacher, type ClassroomTeacher, type Level, type Subject,
} from '../lib/direction';
import { Checkbox } from '@/components/ui/checkbox';

function Initials({ first, last, variant = 'student' }: { first: string; last: string; variant?: 'student' | 'teacher' }) {
  const text = ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
  return (
    <div className={cn(
      "shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-[13px] select-none",
      variant === 'teacher' ? "bg-accent text-primary" : "bg-muted text-muted-foreground"
    )}>
      {text}
    </div>
  );
}

function ActionBtn({ onClick, title, className, children }: { onClick: () => void; title: string; className?: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={cn(
        "grid place-items-center min-w-[44px] min-h-[44px] rounded-md bg-transparent border-none cursor-pointer text-muted-foreground hover:text-foreground hover:bg-muted transition-colors font-[inherit]",
        className
      )}
    >
      {children}
    </button>
  );
}

export default function DirectionClass() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [classes, setClasses] = useState<Classroom[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [teachers, setTeachers] = useState<SchoolTeacher[]>([]);
  const [assigned, setAssigned] = useState<ClassroomTeacher[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const klass = useMemo(() => classes.find((c) => c.id === id || !id) ?? null, [classes, id]);
  const levelLabel = (lid: string | null) => (lid ? levels.find((l) => l.id === lid)?.label ?? null : null);

  const assignedByTeacher = useMemo(() => {
    const groups = new Map<string, ClassroomTeacher[]>();
    for (const a of assigned) {
      const list = groups.get(a.school_teacher_id);
      if (list) list.push(a);
      else groups.set(a.school_teacher_id, [a]);
    }
    return [...groups.entries()].map(([teacherId, items]) => ({ teacherId, items }));
  }, [assigned]);

  const reload = async (sid: string) => {
    const [cls, st, te, at] = await Promise.all([listClassrooms(sid), listStudents(id!), listTeachers(sid), listClassroomTeachers(id!)]);
    setClasses(cls); setStudents(st); setTeachers(te); setAssigned(at);
  };

  const load = useCallback(() => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    setFailed(false);
    getMySchoolId()
      .then(async (sid) => {
        setSchoolId(sid);
        const [, lv, sj] = await Promise.all([
          sid ? reload(sid) : null,
          listLevels().catch(() => [] as Level[]),
          listSubjects().catch(() => [] as Subject[]),
        ]);
        setLevels(lv as Level[]);
        setSubjects(sj as Subject[]);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  /* eslint-disable-next-line react-hooks/exhaustive-deps -- reload stable, run on [id] */
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const teacherName = (tid: string) => {
    const t = teachers.find((x) => x.id === tid);
    return t ? `${t.first_name} ${t.last_name}`.trim() || t.email || 'Enseignant' : 'Enseignant';
  };
  const teacherParts = (tid: string) => {
    const t = teachers.find((x) => x.id === tid);
    return { first: t?.first_name ?? '?', last: t?.last_name ?? '?' };
  };

  /* ---- modal state ---- */
  type ModalKind = null | 'student' | 'move' | 'move-bulk' | 'assign' | 'rename' | 'invite';
  const [modal, setModal] = useState<ModalKind>(null);
  const [saving, setSaving] = useState(false);
  const [sFirst, setSFirst] = useState(''); const [sLast, setSLast] = useState('');
  const [inviteStudent, setInviteStudent] = useState<Student | null>(null); const [inviteEmail, setInviteEmail] = useState('');
  const [moveTarget, setMoveTarget] = useState<Student | null>(null); const [moveClassId, setMoveClassId] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [asgTeacher, setAsgTeacher] = useState(''); const [asgSubjectIds, setAsgSubjectIds] = useState<Set<string>>(new Set());
  const [rnName, setRnName] = useState(''); const [rnLevelId, setRnLevelId] = useState('');

  const openStudent = () => { setSFirst(''); setSLast(''); setModal('student'); };
  const submitStudent = async () => {
    if (!schoolId || !id || saving || (!sFirst.trim() && !sLast.trim())) return;
    setSaving(true);
    try { const s = await addStudent({ classroomId: id, schoolId, firstName: sFirst, lastName: sLast }); setStudents((p) => [...p, s]); setModal(null); }
    finally { setSaving(false); }
  };

  const openMove = (s: Student) => { setMoveTarget(s); setMoveClassId(''); setModal('move'); };
  const submitMove = async () => {
    if (!moveTarget || !moveClassId || saving) return;
    setSaving(true);
    try { await moveStudent(moveTarget.id, moveClassId); setStudents((p) => p.filter((x) => x.id !== moveTarget.id)); setModal(null); }
    finally { setSaving(false); }
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const openMoveBulk = () => { if (selectedIds.size === 0) return; setMoveClassId(''); setModal('move-bulk'); };
  const submitMoveBulk = async () => {
    if (selectedIds.size === 0 || !moveClassId || saving) return;
    setSaving(true);
    try {
      const ids = [...selectedIds];
      const result = await moveStudents(ids, moveClassId);
      setStudents((p) => p.filter((x) => !result.movedIds.includes(x.id)));
      setSelectedIds(new Set());
      setModal(null);
      if (result.failed.length > 0) {
        window.alert(`${result.movedIds.length} élève${result.movedIds.length !== 1 ? 's' : ''} déplacé${result.movedIds.length !== 1 ? 's' : ''}, ${result.failed.length} échec${result.failed.length !== 1 ? 's' : ''}.`);
      }
    } finally { setSaving(false); }
  };

  const openAssign = () => {
    if (!teachers.length) { window.alert("Ajoutez d'abord des enseignants (page Enseignants)."); return; }
    setAsgTeacher(''); setAsgSubjectIds(new Set()); setModal('assign');
  };
  const toggleAsgSubject = (sid: string) => {
    setAsgSubjectIds((p) => {
      const next = new Set(p);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };
  const allSubjectsSelected = subjects.length > 0 && asgSubjectIds.size === subjects.length;
  const toggleAllAsgSubjects = () => setAsgSubjectIds(allSubjectsSelected ? new Set() : new Set(subjects.map((s) => s.id)));
  const submitAssign = async () => {
    if (!schoolId || !id || !asgTeacher || asgSubjectIds.size === 0 || saving) return;
    setSaving(true);
    try {
      const results = await Promise.allSettled(
        [...asgSubjectIds].map((subjectId) => assignTeacher({ classroomId: id, schoolId, teacherId: asgTeacher, subjectId })),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      setAssigned(await listClassroomTeachers(id));
      setModal(null);
      if (failed > 0) window.alert(`${results.length - failed} matière${results.length - failed !== 1 ? 's' : ''} associée${results.length - failed !== 1 ? 's' : ''}, ${failed} échec${failed !== 1 ? 's' : ''}.`);
    } finally { setSaving(false); }
  };

  const openInvite = (s: Student) => { setInviteStudent(s); setInviteEmail(''); setModal('invite'); };
  const submitInvite = async () => {
    if (!inviteStudent || !inviteEmail.trim() || saving) return;
    setSaving(true);
    try {
      await inviteParentOrTutor(inviteEmail.trim(), [inviteStudent.id]);
      setModal(null);
      window.alert(`Invitation envoyée à ${inviteEmail.trim()}.`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    } finally { setSaving(false); }
  };

  const openRename = () => { if (!klass) return; setRnName(klass.name); setRnLevelId(klass.level_id); setModal('rename'); };
  const submitRename = async () => {
    if (!klass || !rnName.trim() || !rnLevelId || saving) return;
    setSaving(true);
    try {
      await updateClassroom(klass.id, { name: rnName.trim(), level_id: rnLevelId });
      setClasses((p) => p.map((c) => (c.id === klass.id ? { ...c, name: rnName.trim(), level_id: rnLevelId } : c)));
      setModal(null);
    } finally { setSaving(false); }
  };

  const onToggleActive = async (s: Student) => {
    await setStudentActive(s.id, !s.active);
    setStudents((p) => p.map((x) => (x.id === s.id ? { ...x, active: !x.active } : x)));
  };
  const onDeleteStudent = async (s: Student) => {
    if (!window.confirm(`Retirer ${s.first_name} ${s.last_name} ? (l'élève est désactivé, pas supprimé)`)) return;
    await deleteStudent(s.id);
    setStudents((p) => p.map((x) => (x.id === s.id ? { ...x, active: false } : x)));
  };
  const onUnassign = async (a: ClassroomTeacher) => { await unassignTeacher(a.id); setAssigned((p) => p.filter((x) => x.id !== a.id)); };
  const onArchive = async () => {
    if (!klass || !window.confirm(`Archiver « ${klass.name} » ?`)) return;
    await updateClassroom(klass.id, { archived: true });
    navigate('/app');
  };
  const onUnarchive = async () => {
    if (!klass) return;
    await updateClassroom(klass.id, { archived: false });
    setClasses((p) => p.map((c) => (c.id === klass.id ? { ...c, archived: false } : c)));
  };

  const otherClasses = classes.filter((c) => c.id !== id && !c.archived).map((c) => ({ value: c.id, label: c.name }));
  const activeStudents = students.filter((s) => s.active);
  const inactiveStudents = students.filter((s) => !s.active);

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <HeaderContent
        width="md"
        showBurger
        backTo="/app"
        backLabel="Tableau de bord"
        actions={klass && (
          <div className="flex gap-2 shrink-0">
            {klass.archived ? (
              <Button variant="outline" size="sm" onClick={() => void onUnarchive()} className="gap-1.5">
                <ArrowClockwise size={13} />
                Désarchiver
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={openRename} className="gap-1.5">
                  <PencilSimple size={13} />
                  Renommer
                </Button>
                <Button variant="outline" size="sm" onClick={onArchive} className="gap-1.5 text-muted-foreground">
                  <Archive size={13} />
                  Archiver
                </Button>
              </>
            )}
          </div>
        )}
      >
        {klass && (
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className={HEADER_TITLE_CLASS}>{klass.name}</h1>
              {klass.archived && (
                <Badge variant="secondary" className="text-[12px] font-bold">Archivée</Badge>
              )}
              {levelLabel(klass.level_id) && (
                <Badge variant="secondary" className="text-[12px] font-bold">{levelLabel(klass.level_id)}</Badge>
              )}
            </div>
            <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
              {activeStudents.length} élève{activeStudents.length !== 1 ? 's' : ''} actif{activeStudents.length !== 1 ? 's' : ''}
              {inactiveStudents.length > 0 && ` · ${inactiveStudents.length} inactif${inactiveStudents.length !== 1 ? 's' : ''}`}
              {assignedByTeacher.length > 0 && ` · ${assignedByTeacher.length} enseignant${assignedByTeacher.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        )}
      </HeaderContent>
      <main className="max-w-[820px] mx-auto px-4 pb-16 pt-4 flex-1 w-full">

        {loading ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-9 w-48 rounded-lg" />
            <Skeleton className="h-[120px] rounded-card" />
            <Skeleton className="h-[200px] rounded-card" />
          </div>
        ) : failed ? (
          <div className="flex flex-col items-start gap-3 py-4">
            <p className="flex items-start gap-2 m-0 text-[13px] text-foreground">
              <WarningCircle size={17} weight="fill" className="text-destructive shrink-0 mt-px" />
              <span>Cette classe n'a pas pu être chargée. Elle est intacte.</span>
            </p>
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={load}>
              <ArrowClockwise size={15} weight="bold" />
              Réessayer
            </Button>
          </div>
        ) : !klass ? (
          <p className="text-muted-foreground py-4">Classe introuvable.</p>
        ) : (
          <>

            <SectionLabel
              title="Enseignants"
              count={assignedByTeacher.length}
              action={
                <Button variant="secondary" size="sm" onClick={openAssign} className="gap-1.5">
                  <Plus size={13} weight="bold" />
                  Associer
                </Button>
              }
            />
            <div className="rounded-card border border-border overflow-hidden shadow-card mb-6">
              {assignedByTeacher.length === 0 ? (
                <div className="flex items-center gap-2 px-4 py-4 text-[13px] text-muted-foreground">
                  <ChalkboardTeacher size={16} className="opacity-40" />
                  Aucun enseignant associé.
                </div>
              ) : assignedByTeacher.map((group, i) => {
                const { first, last } = teacherParts(group.teacherId);
                return (
                  <div key={group.teacherId}>
                    {i > 0 && <Separator />}
                    <div className="flex items-start gap-3 px-4 py-2.5">
                      <Initials first={first} last={last} variant="teacher" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[14px] text-foreground mb-0.5">{teacherName(group.teacherId)}</div>
                        {group.items.map((a) => (
                          <div key={a.id} className="flex items-center justify-between gap-2">
                            <span className="text-[12px] text-muted-foreground">{a.subject_label || 'Matière non précisée'}</span>
                            <ActionBtn
                              onClick={() => onUnassign(a)}
                              title={`Retirer de ${a.subject_label || 'cette matière'}`}
                              className="text-muted-foreground -mr-2"
                            >
                              <X size={14} />
                            </ActionBtn>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <SectionLabel
              title="Élèves"
              count={students.length}
              action={
                selectedIds.size > 0 ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] text-muted-foreground">{selectedIds.size} sélectionné{selectedIds.size !== 1 ? 's' : ''}</span>
                    <Button variant="outline" size="sm" onClick={clearSelection}>Annuler</Button>
                    <Button variant="secondary" size="sm" onClick={openMoveBulk} className="gap-1.5" disabled={otherClasses.length === 0}>
                      <ArrowsLeftRight size={13} />
                      Déplacer la sélection
                    </Button>
                  </div>
                ) : (
                  <Button variant="secondary" size="sm" onClick={openStudent} className="gap-1.5">
                    <Plus size={13} weight="bold" />
                    Ajouter
                  </Button>
                )
              }
            />
            <div className="rounded-card border border-border overflow-hidden shadow-card">
              {students.length === 0 ? (
                <div className="px-4 py-4 text-[13px] text-muted-foreground">Aucun élève. Ajoutez-en.</div>
              ) : students.map((s, i) => (
                <div key={s.id}>
                  {i > 0 && <Separator />}
                  <div className={cn(
                    "flex items-center gap-3 px-4 py-2.5 transition-colors",
                    !s.active && "opacity-50"
                  )}>
                    <Checkbox
                      checked={selectedIds.has(s.id)}
                      onCheckedChange={() => toggleSelected(s.id)}
                      aria-label={`Sélectionner ${s.first_name} ${s.last_name}`}
                    />
                    <Initials first={s.first_name} last={s.last_name} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-[14px] text-foreground">
                          {s.first_name} {s.last_name}
                        </span>
                        {!s.active && (
                          <Badge variant="outline" className="text-[11px] text-muted-foreground px-1.5 py-0">inactif</Badge>
                        )}
                        <span className="text-[11px] text-muted-foreground font-mono">{s.code}</span>
                      </div>
                      {s.email && <div className="text-[12px] text-muted-foreground truncate">{s.email}</div>}
                    </div>
                    <div className="flex items-center shrink-0 -mr-1">
                      <ActionBtn onClick={() => openInvite(s)} title="Inviter un parent / tuteur">
                        <EnvelopeSimple size={15} />
                      </ActionBtn>
                      <ActionBtn onClick={() => openMove(s)} title="Déplacer dans une autre classe">
                        <ArrowsLeftRight size={15} />
                      </ActionBtn>
                      <ActionBtn onClick={() => void onToggleActive(s)} title={s.active ? 'Désactiver' : 'Réactiver'}>
                        {s.active ? <Pause size={15} /> : <Play size={15} />}
                      </ActionBtn>
                      <ActionBtn onClick={() => void onDeleteStudent(s)} title="Retirer l'élève" className="hover:text-destructive">
                        <X size={15} />
                      </ActionBtn>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      <Modal open={modal === 'student'} title="Ajouter un élève" onClose={() => setModal(null)}
        footer={<><Button variant="secondary" onClick={() => setModal(null)}>Annuler</Button><Button onClick={submitStudent} disabled={saving || (!sFirst.trim() && !sLast.trim())}>{saving ? '…' : 'Ajouter'}</Button></>}>
        <FormField label="Prénom" value={sFirst} onChange={setSFirst} autoFocus placeholder="Prénom" />
        <FormField label="Nom" value={sLast} onChange={setSLast} placeholder="Nom" onEnter={submitStudent} />
      </Modal>

      <Modal open={modal === 'move'} title={`Déplacer ${moveTarget?.first_name ?? ''}`} onClose={() => setModal(null)}
        footer={<><Button variant="secondary" onClick={() => setModal(null)}>Annuler</Button><Button onClick={submitMove} disabled={!moveClassId || saving}>Déplacer</Button></>}>
        {otherClasses.length === 0 ? <p className="text-muted-foreground text-[13px]">Aucune autre classe disponible.</p>
          : <SelectField label="Classe de destination" value={moveClassId} onChange={setMoveClassId} options={otherClasses} placeholder="Choisir une classe…" />}
      </Modal>

      <Modal open={modal === 'move-bulk'} title={`Déplacer ${selectedIds.size} élève${selectedIds.size !== 1 ? 's' : ''}`} onClose={() => setModal(null)}
        footer={<><Button variant="secondary" onClick={() => setModal(null)}>Annuler</Button><Button onClick={submitMoveBulk} disabled={!moveClassId || saving}>Déplacer</Button></>}>
        {otherClasses.length === 0 ? <p className="text-muted-foreground text-[13px]">Aucune autre classe disponible.</p>
          : <SelectField label="Classe de destination" value={moveClassId} onChange={setMoveClassId} options={otherClasses} placeholder="Choisir une classe…" />}
      </Modal>

      <Modal open={modal === 'assign'} title="Associer un enseignant" onClose={() => setModal(null)}
        footer={<><Button variant="secondary" onClick={() => setModal(null)}>Annuler</Button><Button onClick={submitAssign} disabled={!asgTeacher || asgSubjectIds.size === 0 || saving}>Associer</Button></>}>
        <SelectField label="Enseignant" value={asgTeacher} onChange={setAsgTeacher} options={teachers.map((t) => ({ value: t.id, label: `${t.first_name} ${t.last_name}`.trim() || t.email || 'Enseignant' }))} placeholder="Choisir un enseignant…" />
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-semibold text-foreground">Matières</span>
            <Button type="button" variant="outline" size="sm" onClick={toggleAllAsgSubjects}>
              {allSubjectsSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
            </Button>
          </div>
          <div className="flex flex-col gap-1 max-h-60 overflow-y-auto">
            {subjects.map((s) => (
              <label key={s.id} className="flex items-center gap-3 min-h-11 cursor-pointer text-[14px] text-foreground">
                <Checkbox checked={asgSubjectIds.has(s.id)} onCheckedChange={() => toggleAsgSubject(s.id)} />
                {s.label}
              </label>
            ))}
          </div>
        </div>
      </Modal>

      <Modal open={modal === 'invite'} title={`Inviter un parent / tuteur${inviteStudent ? ` — ${inviteStudent.first_name} ${inviteStudent.last_name}` : ''}`} onClose={() => setModal(null)}
        footer={<><Button variant="secondary" onClick={() => setModal(null)}>Annuler</Button><Button onClick={submitInvite} disabled={saving || !inviteEmail.trim()}>{"Envoyer l'invitation"}</Button></>}>
        <FormField label="Email du parent / tuteur" value={inviteEmail} onChange={setInviteEmail} autoFocus placeholder="email" type="email" onEnter={submitInvite} />
      </Modal>

      <Modal open={modal === 'rename'} title="Modifier la classe" onClose={() => setModal(null)}
        footer={<><Button variant="secondary" onClick={() => setModal(null)}>Annuler</Button><Button onClick={submitRename} disabled={!rnName.trim() || !rnLevelId || saving}>Enregistrer</Button></>}>
        <FormField label="Nom de la classe" value={rnName} onChange={setRnName} autoFocus onEnter={submitRename} />
        <SelectField label="Niveau" value={rnLevelId} onChange={setRnLevelId} options={levels.map((l) => ({ value: l.id, label: l.label }))} placeholder="Choisir un niveau…" />
      </Modal>
    </div>
  );
}

function SectionLabel({ title, count, action }: { title: string; count: number; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-muted-foreground">{title}</span>
        <span className="text-[11px] font-bold bg-muted text-muted-foreground rounded-full px-1.5 py-0 tabular-nums">{count}</span>
      </div>
      {action}
    </div>
  );
}
